import { ArrowRight, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import hero246 from "@/assets/gallery/246.webp";
import hero074 from "@/assets/gallery/074.webp";
import hero261 from "@/assets/gallery/261.webp";
import hero300 from "@/assets/gallery/300.webp";
import hero140 from "@/assets/gallery/140.webp";
import hero004 from "@/assets/gallery/004.webp";
import hero246Mobile from "@/assets/home/246-m.webp";
import hero074Mobile from "@/assets/home/074-m.avif";
import hero074MobileFallback from "@/assets/home/074-m.webp";
import hero261Mobile from "@/assets/home/261-m.webp";
import hero300Mobile from "@/assets/home/300-m.webp";
import hero140Mobile from "@/assets/home/140-m.webp";
import hero004Mobile from "@/assets/home/004-m.webp";
import { openBooking } from "./openBooking";

// Mobile files are cropped to the tall hero and compressed to the bytes-per-pixel
// budget Chrome uses. Desktop keeps the original frame.
type HeroSlide = {
  desktop: string;
  mobile: string;
  mobileType: string;
  fallback?: string;
  width: number;
  height: number;
};

const heroSlides: HeroSlide[] = [
  { desktop: hero246, mobile: hero246Mobile, mobileType: "image/webp", width: 1600, height: 900 },
  {
    desktop: hero074,
    mobile: hero074Mobile,
    mobileType: "image/avif",
    fallback: hero074MobileFallback,
    width: 1600,
    height: 900,
  },
  { desktop: hero261, mobile: hero261Mobile, mobileType: "image/webp", width: 900, height: 1600 },
  { desktop: hero300, mobile: hero300Mobile, mobileType: "image/webp", width: 900, height: 1600 },
  { desktop: hero140, mobile: hero140Mobile, mobileType: "image/webp", width: 1600, height: 900 },
  { desktop: hero004, mobile: hero004Mobile, mobileType: "image/webp", width: 900, height: 1600 },
];

export function Hero() {
  const [index, setIndex] = useState(0);
  const [previous, setPrevious] = useState<number | null>(null);

  useEffect(() => {
    let interval = 0;
    // Keep the first mobile paint to a single hero image. The other slides
    // start after the LCP image has had time to load.
    const start = window.setTimeout(() => {
      interval = window.setInterval(() => {
        setIndex((current) => {
          setPrevious(current);
          return (current + 1) % heroSlides.length;
        });
      }, 3000);
    }, 8000);
    return () => {
      window.clearTimeout(start);
      window.clearInterval(interval);
    };
  }, []);

  const visible = previous === null ? [index] : [previous, index];

  return (
    <section className="relative isolate overflow-hidden bg-ink text-cream">
      {visible.map((i) => {
        const slide = heroSlides[i];
        const eager = i === index && previous === null;
        return (
          <picture key={slide.desktop} className="absolute inset-0 -z-10">
            <source media="(max-width: 767px)" srcSet={slide.mobile} type={slide.mobileType} />
            {slide.fallback ? (
              <source media="(max-width: 767px)" srcSet={slide.fallback} type="image/webp" />
            ) : null}
            <img
              src={slide.desktop}
              alt="Custom tile and stone installation by RKD Tile"
              className={`h-full w-full object-cover ${previous !== null ? "transition-opacity duration-1000 ease-in-out " : ""}${i === index ? "opacity-55" : "opacity-0"}`}
              width={slide.width}
              height={slide.height}
              decoding={eager ? "sync" : "async"}
              loading={eager ? "eager" : "lazy"}
              fetchPriority={eager ? "high" : "low"}
              {...{ fetchpriority: eager ? "high" : "low" }}
            />
          </picture>
        );
      })}
      <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-hero)" }} />
      <div className="container-prose relative py-24 md:py-36 lg:py-44">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="gold-rule" />
            <span className="text-xs tracking-[0.25em] uppercase text-accent font-semibold">
              Established 1996 · BBB A+ · Builder's Choice 3×
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] text-cream">
            Utah's Most Trusted <em className="not-italic text-accent">Custom Tile</em> Craftsmen.
          </h1>
          <p className="mt-6 max-w-xl text-lg md:text-xl text-cream/80 leading-relaxed">
            Showers, backsplashes, fireplaces, mosaics, and natural stone floors — RKD Tile has set
            Utah's standard for custom tile work for 30 years. Done right the first time.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button
              type="button"
              onClick={openBooking}
              className="inline-flex items-center gap-2 rounded-sm bg-accent px-7 py-4 text-sm font-semibold uppercase tracking-wider text-accent-foreground hover:bg-accent/90 transition-colors"
            >
              Schedule a Consultation <ArrowRight className="h-4 w-4" />
            </button>
            <a
              href="tel:8015982226"
              className="inline-flex items-center gap-2 rounded-sm border border-cream/30 px-7 py-4 text-sm font-semibold uppercase tracking-wider text-cream hover:bg-cream/10 transition-colors"
            >
              <Phone className="h-4 w-4" /> (801) 598-2226
            </a>
          </div>
          <div className="mt-14 grid grid-cols-3 gap-6 max-w-md border-t border-cream/20 pt-8">
            <Stat value="30+" label="Years Crafting" />
            <Stat value="A+" label="BBB Rating" />
            <Stat value="24h" label="Response Time" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="font-display text-3xl md:text-4xl text-accent">{value}</div>
      <div className="text-[11px] tracking-[0.18em] uppercase text-cream/70 mt-1">{label}</div>
    </div>
  );
}
